import React from 'react';
import { BundleBreakdown, breakdownKr, discountOnHighestRate, discountRuleText } from '../../services/bundles';
import { Coffee } from 'lucide-react';
import { BundlePart } from '../../services/bundles';
import { StoreImage } from '../ui/StoreImage';

/** Det butiken behöver veta om ett paket: delarna, värdet och besparingen. */
export interface StoreBundleInfo {
    parts: BundlePart[];
    /** Summan av delarnas egna priser. */
    value: number;
    /** Värdet minus paketets pris. Visas bara när den är större än noll. */
    savings: number;
}

/** "Det här ingår" på produktsidan, i samma stil som rostningskortet. */
export const BundleContents: React.FC<{ bundle: StoreBundleInfo; className?: string }> = ({ bundle, className = 'mb-10' }) => (
    <div className={`bg-stone-50 p-5 rounded-3xl border border-stone-100 ${className}`}>
        <div className="text-xs font-bold uppercase tracking-widest text-[#1A120B] mb-2">Det här ingår</div>
        <ul>
            {bundle.parts.map((part, i) => (
                <li key={`${part.productId}-${part.variantId || ''}-${i}`} className="flex items-center gap-3 py-2.5 border-t border-stone-200/70 first:border-t-0">
                    <div className="w-11 h-11 rounded-xl overflow-hidden bg-white border border-stone-100 flex-shrink-0 flex items-center justify-center text-stone-300">
                        {part.imageUrl ? <StoreImage src={part.imageUrl} width={128} alt="" className="w-full h-full object-cover" /> : <Coffee size={18} />}
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold text-stone-900 leading-tight">{part.name}</p>
                        <p className="text-xs text-stone-500">{part.variantName ? `${part.variantName} · ` : ''}ordinarie {part.unitPrice} kr</p>
                    </div>
                    <span className="text-sm font-bold text-stone-900 whitespace-nowrap">{part.quantity} st</span>
                </li>
            ))}
        </ul>
    </div>
);

/**
 * Paketets uppdelning: varje del med ordinarie pris, andel av paketpriset och
 * moms, och paketrabatten. Samma siffror som kvittot och fakturan.
 */
export const BundleBreakdownList: React.FC<{ breakdown: BundleBreakdown; className?: string }> = ({ breakdown, className = '' }) => (
    <ul className={`space-y-1 text-[11px] leading-snug text-stone-500 list-none p-0 ${className}`}>
        {breakdown.rows.map((r, i) => (
            // Får inte namn och priser plats på en rad hamnar priserna på raden under.
            <li key={i} className="flex flex-wrap justify-between gap-x-3">
                <span>{r.label}</span>
                <span className="ml-auto whitespace-nowrap tabular-nums text-right">
                    <span className="line-through opacity-70" aria-label={`ordinarie ${breakdownKr(r.ordinary)}`}>{breakdownKr(r.ordinary)}</span>
                    {' '}<span className="font-semibold text-stone-700">{breakdownKr(r.allocated)}</span>
                    {r.vatRate !== null && <span> · {r.vatRate} % moms</span>}
                </span>
            </li>
        ))}
        {breakdown.discount > 0 && (
            <li className="pt-1 border-t border-dashed border-stone-200 flex justify-between items-start gap-3">
                <span>Paketrabatt, {discountRuleText(discountOnHighestRate(breakdown.rows))}</span>
                <span className="whitespace-nowrap tabular-nums font-semibold text-emerald-700">−{breakdownKr(breakdown.discount)}</span>
            </li>
        )}
    </ul>
);
